import { ButtonCus } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';
import yup from '@/services/yup';
import { Other } from '@/types/system-setting';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface SettingOtherProps {
    other?: Other,
    onOrderSubmit?: (other: Other) => void
}

const SettingOther = ({ other, onOrderSubmit }: SettingOtherProps) => {
    // hook
    const { t } = useTranslation();

    const validateSchema = yup.object().shape({
        paymentTime: yup
            .string()
            .trim()
            .checkInputOnlyPositiveDigitsOrEmpty(t('form_error_validate.only_number'))
            .required('form_error_validate.required'),
        warningTime: yup
            .string()
            .trim()
            .checkInputOnlyPositiveDigitsOrEmpty(t('form_error_validate.only_number'))
            .required('form_error_validate.required')
            .test('is-greater', t('form_error_validate.warning_time_error'), function (value) {
                const paymentTime = Number(this.parent.paymentTime);
                const warningTime = Number(value);
                if (warningTime === undefined || warningTime <= 0) {
                    return true;
                } else {
                    return warningTime > paymentTime;
                }
            }),
        returnTime: yup
            .string()
            .trim()
            .checkInputOnlyPositiveDigitsOrEmpty(t('form_error_validate.only_number'))
            .required('form_error_validate.required')
    });

    type OtherFormData = yup.InferType<typeof validateSchema>;

    const form = useForm<OtherFormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            paymentTime: '',
            warningTime: '',
            returnTime: ''
        }
    });

    // lifecycle
    useEffect(() => {
        form.reset({
            paymentTime: other?.paymentTimeBeforeBikePickup?.toString(),
            warningTime: other?.lateTransferWarningTime?.toString(),
            returnTime: other?.lateBikeReturnTime?.toString()
        });
    }, [other]);

    const onSubmit = async (data: OtherFormData) => {
        const {
            paymentTime: paymentStr,
            warningTime: warnignStr,
            returnTime: returnStr
        } = data;

        const paymentTime = Number(paymentStr);
        const warningTime = Number(warnignStr);
        const returnTime = Number(returnStr);
        const validData: Other = {};

        if (Number.isInteger(paymentTime) && paymentTime >= 0) validData.paymentTimeBeforeBikePickup = paymentTime;
        if (Number.isInteger(warningTime) && warningTime >= 0) validData.lateTransferWarningTime = warningTime;
        if (Number.isInteger(returnTime) && returnTime >= 0) validData.lateBikeReturnTime = returnTime;
        if (onOrderSubmit) {
            onOrderSubmit(validData);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-[30px]'>
                <div className='lg:w-2/3 xl:w-1/2'>
                    <FormField
                        control={form.control}
                        name='paymentTime'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('system_setting.setting_other.payment_time_title')}</FormLabel>
                                <div className='flex gap-2'>
                                    <FormControl className='flex-shrink'>
                                        <FormatNumberVer2
                                            decimalScale={0}
                                            statusDisplay='input'
                                            thousandSeparator={true}
                                            allowNegative={false}
                                            placeholder={t('placeholder.enter')}
                                            value={field.value}
                                            setvalueOfNumericFormat={(values) => {
                                                field.onChange(values);
                                            }}
                                            suffix='時間'
                                        />
                                    </FormControl>
                                    <p className='flex-shrink-0 self-center text-black-1'>{t('system_setting.setting_other.hours_before_pick_up')}</p>
                                </div>
                                <FormMessage />
                                {/* <div className='flex mt-2 gap-2'>
                                    <p className='flex-shrink-0'>{t('system_setting.setting_other.note')}</p>
                                    <p className='whitespace-pre-line'>{t('system_setting.setting_other.payment_time_note')}</p>
                                </div> */}
                            </FormItem>
                        )}
                    />
                </div>
                {/* <hr /> */}
                <div className='lg:w-2/3 xl:w-1/2'>
                    <FormField
                        control={form.control}
                        name='warningTime'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('system_setting.setting_other.warning_time_title')}</FormLabel>
                                <div className='flex gap-2'>
                                    <FormControl className='flex-shrink'>
                                        <FormatNumberVer2
                                            decimalScale={0}
                                            statusDisplay='input'
                                            thousandSeparator={true}
                                            allowNegative={false}
                                            placeholder={t('placeholder.enter')}
                                            value={field.value}
                                            setvalueOfNumericFormat={(values) => {
                                                field.onChange(values);
                                            }}
                                            suffix='時間'
                                        />
                                    </FormControl>
                                    <p className='flex-shrink-0 self-center text-black-1'>
                                        {t('system_setting.setting_other.hours_before_pick_up')}
                                    </p>
                                </div>
                                <FormMessage />
                                {/* <div className='flex mt-2 gap-2'>
                                    <p className='flex-shrink-0'>{t('system_setting.setting_other.note')}</p>
                                    <p className='whitespace-pre-line'>{t('system_setting.setting_other.warning_time_note')}</p>
                                </div> */}
                            </FormItem>
                        )}
                    />
                </div>
                {/* <hr /> */}
                <div className='lg:w-2/3 xl:w-1/2'>
                    <FormField
                        control={form.control}
                        name='returnTime'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('system_setting.setting_other.return_time_title')}</FormLabel>
                                <div className='flex gap-2'>
                                    <FormControl className='flex-shrink'>
                                        <FormatNumberVer2
                                            decimalScale={0}
                                            statusDisplay='input'
                                            thousandSeparator={true}
                                            allowNegative={false}
                                            placeholder={t('placeholder.enter')}
                                            value={field.value}
                                            setvalueOfNumericFormat={(values) => {
                                                field.onChange(values);
                                            }}
                                            suffix='時間'
                                        />
                                    </FormControl>
                                    <p className='flex-shrink-0 self-center text-black-1'>{t('system_setting.setting_other.hours_end_of_bike_retal')}</p>
                                </div>
                                <FormMessage />
                                {/* <div className='flex mt-2 gap-2'>
                                    <p className='flex-shrink-0'>{t('system_setting.setting_other.note')}</p>
                                    <p className='whitespace-pre-line'>{t('system_setting.setting_other.return_time_note')}</p>
                                </div> */}
                            </FormItem>
                        )}
                    />
                </div>
                <ButtonCus variant='default' size='sm' type='submit' className='self-center'>{t('button.save')}</ButtonCus>
            </form>
        </Form>
    );
};

export default SettingOther;
